import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../api/api';
import { SemiFinishedProduct, SemiFinishedCategory } from '../../types';

interface SemiFinishedState {
  items: SemiFinishedProduct[];
  categories: SemiFinishedCategory[];
  loading: boolean;
}

const initialState: SemiFinishedState = { items: [], categories: [], loading: false };
type BulkDeleteResult = { deletedIds: number[]; errors: string[] };

export const fetchSemiFinished = createAsyncThunk('semiFinished/fetchAll', async () => {
  const res = await api.get<SemiFinishedProduct[]>('/semifinishedproducts');
  return res.data;
});

export const createSemiFinished = createAsyncThunk('semiFinished/create', async (data: any) => {
  const res = await api.post<SemiFinishedProduct>('/semifinishedproducts', data);
  return res.data;
});

export const updateSemiFinished = createAsyncThunk(
  'semiFinished/update',
  async ({ id, data }: { id: number; data: any }) => {
    const res = await api.put<SemiFinishedProduct>(`/semifinishedproducts/${id}`, data);
    return res.data;
  }
);

export const deleteSemiFinished = createAsyncThunk('semiFinished/delete', async (id: number) => {
  await api.delete(`/semifinishedproducts/${id}`);
  return id;
});

export const bulkDeleteSemiFinished = createAsyncThunk('semiFinished/bulkDelete', async (ids: number[]) => {
  const res = await api.post<BulkDeleteResult>('/semifinishedproducts/bulk-delete', { ids });
  return res.data;
});

export const fetchSemiFinishedCategories = createAsyncThunk('semiFinished/fetchCategories', async () => {
  const res = await api.get<SemiFinishedCategory[]>('/semifinishedcategories');
  return res.data;
});

export const createSemiFinishedCategory = createAsyncThunk('semiFinished/createCategory', async (data: any) => {
  const res = await api.post<SemiFinishedCategory>('/semifinishedcategories', data);
  return res.data;
});

export const updateSemiFinishedCategory = createAsyncThunk(
  'semiFinished/updateCategory',
  async ({ id, data }: { id: number; data: any }) => {
    const res = await api.put<SemiFinishedCategory>(`/semifinishedcategories/${id}`, data);
    return res.data;
  }
);

export const deleteSemiFinishedCategory = createAsyncThunk('semiFinished/deleteCategory', async (id: number) => {
  await api.delete(`/semifinishedcategories/${id}`);
  return id;
});

const semiFinishedSlice = createSlice({
  name: 'semiFinished',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSemiFinished.pending, (state) => { state.loading = true; })
      .addCase(fetchSemiFinished.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(fetchSemiFinished.rejected, (state) => { state.loading = false; })
      .addCase(createSemiFinished.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      .addCase(updateSemiFinished.fulfilled, (state, action) => {
        const idx = state.items.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
      })
      .addCase(deleteSemiFinished.fulfilled, (state, action) => {
        state.items = state.items.filter((i) => i.id !== action.payload);
      })
      .addCase(bulkDeleteSemiFinished.fulfilled, (state, action) => {
        const deleted = new Set(action.payload.deletedIds);
        state.items = state.items.filter((i) => !deleted.has(i.id));
      })
      .addCase(fetchSemiFinishedCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
      })
      .addCase(createSemiFinishedCategory.fulfilled, (state, action) => {
        state.categories.push(action.payload);
      })
      .addCase(updateSemiFinishedCategory.fulfilled, (state, action) => {
        const idx = state.categories.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.categories[idx] = action.payload;
      })
      .addCase(deleteSemiFinishedCategory.fulfilled, (state, action) => {
        state.categories = state.categories.filter((i) => i.id !== action.payload);
      });
  },
});

export default semiFinishedSlice.reducer;
